import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import { updateComplianceState } from "@/api/taxRules.js";

const DISCLAIMER_TYPES = ["DISPLAY", "ACTION"];

function PurchaseAttestationModal({ isOpen, setIsOpen, state, stateName, onSave }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [disclaimerType, setDisclaimerType] = useState("DISPLAY");
  const [disclaimer, setDisclaimer] = useState("");

  const isEditMode = Boolean(state?.legal_disclaimer);

  useEffect(() => {
    if (!isOpen) return;
    setDisclaimerType(state?.legal_disclaimer_type || "DISPLAY");
    setDisclaimer(state?.legal_disclaimer || "");
  }, [isOpen, state]);

  const handleSave = async () => {
    if (!state?.code) return;
    if (!disclaimer.trim()) {
      dispatch(addToast({ message: "Disclaimer text is required", type: "error" }));
      return;
    }
    try {
      setSaving(true);
      await updateComplianceState(state.code, {
        legal_disclaimer_type: disclaimerType,
        legal_disclaimer: disclaimer,
      });
      dispatch(addToast({
        message: isEditMode ? "Purchase attestation updated" : "Purchase attestation created",
        type: "success",
      }));
      onSave?.();
      setIsOpen(false);
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to save purchase attestation",
        type: "error",
      }));
    } finally {
      setSaving(false);
    }
  };

  const title = `${isEditMode ? "Edit" : "Create"} Purchase Attestation${stateName ? ` — ${stateName}` : ""}`;

  return (
    <ModalBasic
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      title={title}
      width="xl"
    >
      <div className="px-5 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="disclaimer-type">
            Disclaimer Type <span className="text-rose-500">*</span>
          </label>
          <select
            id="disclaimer-type"
            value={disclaimerType}
            onChange={(e) => setDisclaimerType(e.target.value)}
            className="form-select w-full"
          >
            {DISCLAIMER_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            DISPLAY shows the disclaimer to the shopper. ACTION requires explicit acknowledgement.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="disclaimer-text">
            Disclaimer <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="disclaimer-text"
            value={disclaimer}
            onChange={(e) => setDisclaimer(e.target.value)}
            rows={6}
            className="form-textarea w-full"
            placeholder="Enter the legal disclaimer text shown to shoppers in this state..."
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {"You can use ${name}, ${city}, ${state}, and ${zip} as variables in this field."}
          </p>
        </div>
      </div>

      <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
        <SecondaryButton onClick={() => setIsOpen(false)} disabled={saving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleSave} loading={saving} disabled={saving}>
          {isEditMode ? "Save" : "Create"}
        </PrimaryButton>
      </div>
    </ModalBasic>
  );
}

export default PurchaseAttestationModal;
